export interface CustomError extends Error {
  status?: number;
  response?: unknown;
  statusText?: string;
}
export const defaultHeaders = {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  Accept: 'application/json',
  'Content-Type': 'application/json',
};

export const defaultOptions = { mode: 'cors' };

export const fetchJson = async <T>(url: string, options?: Record<string, any>): Promise<T> => {
  const newOptions: any = { ...defaultOptions, ...options };
  newOptions.headers = { ...defaultHeaders, ...newOptions.headers };

  try {
    const response = await fetch(url, newOptions);
    if (!response.ok) {
      const errorMessage = `HTTP error! Status: ${response.status}, Status Text: ${
        response.statusText
      }, URL: ${url}, Headers: ${JSON.stringify(Array.from(response.headers.entries()))}`;

      let errorResponse: unknown;
      try {
        errorResponse = await response.json();
      } catch {
        // If JSON parsing fails, use a fallback object so downstream code has context
        errorResponse = { error: response.statusText } as const;
      }

      const extractStatusText = (er: unknown, fallback: string): string => {
        if (er && typeof er === 'object') {
          const maybe = er as { detail?: unknown; error?: unknown };
          if (typeof maybe.detail === 'string') return maybe.detail;
          if (typeof maybe.error === 'string') return maybe.error;
        }
        return fallback;
      };

      const error: CustomError = new Error(errorMessage);
      error.status = response.status;
      error.response = errorResponse;
      error.statusText = extractStatusText(errorResponse, response.statusText);
      throw error;
    }
    return (await response.json()) as T;
  } catch (error) {
    const customError = error as CustomError;

    if (customError && typeof customError === 'object' && customError.status) {
      throw customError;
    } else {
      console.trace(
        `[TRACE] There was a problem fetching the data from the URL: ${url}`,
        customError,
      );

      throw new Error(
        `There was a problem fetching the data from the URL: ${url}. Original error: ${customError.message}`,
      );
    }
  }
};
